import { processFormThumbnail } from "@utils/image-upload";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import { type BasicOptionType } from "@components/atoms/CheckBox";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import RenderBannerExposureLocationCheckbox from "@components/molecules/RenderBannerExposureLocationCheckbox";
import RenderBannerConnectionList from "@components/molecules/RenderBannerConnectionList";
import SizeNoticeBox from "@components/molecules/SizeNoticeBox";
import regex from "@constants/regex";
import {
    type V2BannerCreateRequestDto,
    useV2BannersControllerCreateBanner,
    V2BannerCreateRequestDtoCategory as BannerCategory,
    V2BannerCreateRequestDtoLinkType as LinkType,
    type V2BannerCreateRequestDtoPositionsItem as PositionsItem,
} from "@everon-it/api-client/contents-gw";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import useRouter from "@hooks/useRouter";
import useAuthStore from "@store/auth";
import { colors, typo } from "@styles/index";
import { RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import { useFormik } from "formik";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { BannerCreateData, type BannerCreateInitValuesType } from "./Data";

export const ConnectionCategory: BasicOptionType<LinkType>[] = [
    { label: "공지사항", value: LinkType.NOTICE },
    { label: "이벤트", value: LinkType.EVENT },
    { label: "URL 연결", value: LinkType.URL },
];

const BannerCreate: React.FC = () => {
    // Props
    const HEADER_TITLE = "배너 등록";
    const CONTENT_TITLE = "배너 내용";

    // Data
    const { contentsOption, formInitialValues } = BannerCreateData;
    const [connectionKindList, setConnectionKindList] = useState<BasicOptionType<LinkType>>(ConnectionCategory[0]);

    // Hooks
    const { goBack } = useRouter();
    const { showConfirmModal, hideModal } = useGlobalModal();
    const { showConfirmNoti } = useGlobalNoti();
    const { userInfo } = useAuthStore();

    // api-client: 생성 mutation
    const { mutate: createMutate } = useV2BannersControllerCreateBanner({
        mutation: {
            onSuccess: () => {
                showConfirmNoti({});
                goBack();
            },
        },
    });

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: async values => {
            // 이미지 업로드 처리
            const imageUrl = await processFormThumbnail(values.imageUrl, "banner");

            // positions에서 "ALL" 제외
            const filteredPositions = values.positions.filter((p): p is PositionsItem => p !== "ALL");

            const body: V2BannerCreateRequestDto = {
                category: values.category,
                displayStartAt: values.displayStartAt,
                displayEndAt: values.displayEndAt,
                isVisible: values.isVisible,
                title: values.title,
                imageUrl: imageUrl,
                linkType: values.linkType,
                linkTargetId: values.linkTargetId ?? undefined,
                linkUrl: values.linkUrl || undefined,
                positions: filteredPositions,
                createdBy: userInfo?.name ?? "",
            };
            createMutate({ data: body });
        },
    });

    const handleGoBackClick = () => {
        const { displayEndAt, title, imageUrl, linkTargetId, positions } = formik.values;

        if (displayEndAt || title || imageUrl || linkTargetId || positions.length > 0) {
            showConfirmModal({
                title: `작성한 내용을 저장하지 않고\n 취소하시겠어요?`,
                firstButtonText: "닫기",
                secondButtonText: "확인",
                onConfirm: () => {
                    hideModal();
                    goBack();
                },
            });
        } else {
            goBack();
        }
    };

    const handleCreateContent = () => {
        formik.handleSubmit();
    };

    const handleBannerGrideDownLoad = () => {
        const pdfUrl = process.env.PUBLIC_URL + `/pdf/${"promotion-guide"}.pdf`;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `${"promotion-guide"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const createButtonValidate = useCallback(() => {
        const { category, displayStartAt, displayEndAt, title, imageUrl, linkType, linkTargetId, linkUrl, positions } = formik.values;
        const urlRegex = regex.url;
        const isUrl = linkUrl && urlRegex.test(linkUrl);
        const isContentUrl = linkType === LinkType.URL;

        return !displayStartAt || !displayEndAt || !title || !imageUrl || (isContentUrl ? !linkUrl || !isUrl : !linkTargetId) || (category === BannerCategory.BANNER && !positions.length);
    }, [formik.values]);

    // 배너 종류 바꼈을때 연결 목록 변경 및 데이터 초기화
    useEffect(() => {
        const initValue: BannerCreateInitValuesType = {
            ...formik.initialValues,
            displayStartAt: moment().format("YYYY-MM-DD"),
            category: formik.values.category,
        };

        setConnectionKindList(ConnectionCategory[0]);
        void formik.setValues(initValue);
    }, [formik.values.category]);

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>
            <div>
                <ContentBox style={{ padding: 0 }}>
                    <ContentsHeader>
                        {CONTENT_TITLE}
                        <Button buttonSize="small" buttonType="line_black" text="배너 가이드 다운받기" onClick={handleBannerGrideDownLoad} />
                    </ContentsHeader>
                    <ContentsMainWrap>
                        {contentsOption?.map(({ label, name, options, hasKeywordToShowContent, isSizeQuestion, size, ...res }, _) => {
                            if (hasKeywordToShowContent) {
                                if (formik.values.category === hasKeywordToShowContent) {
                                    return <RenderBannerExposureLocationCheckbox key={`${_}_contents`} label={label} formik={formik} name={name} checkBoxOptions={options ?? []} />;
                                }
                            }

                            if (!hasKeywordToShowContent) {
                                const bannerSizeCheck = formik.values.category === BannerCategory.POPUP ? size : "small";

                                return (
                                    <React.Fragment key={`${_}_contents`}>
                                        <InputWrap>
                                            <LabelWrap>
                                                <LabelInnerWrap>
                                                    <Label>{label || "LABEL"}</Label>
                                                    {isSizeQuestion && <SizeNoticeBox size={bannerSizeCheck} />}
                                                </LabelInnerWrap>
                                            </LabelWrap>
                                            <InputContentsWrap>
                                                {RenderCommonElementByType({
                                                    formik,
                                                    name,
                                                    options,
                                                    size: bannerSizeCheck,
                                                    ...res,
                                                })}
                                            </InputContentsWrap>
                                        </InputWrap>
                                    </React.Fragment>
                                );
                            }
                        })}

                        {RenderBannerConnectionList({
                            formik,
                            connectionKindList,
                            setConnectionKindList,
                        })}
                    </ContentsMainWrap>
                </ContentBox>
                <FooterButtonWrap>
                    <LeftButtonWrap></LeftButtonWrap>
                    <RightButtonWrap>
                        <Button text="취소" buttonType="line_black" onClick={handleGoBackClick} />
                        <Button disabled={createButtonValidate()} text="등록하기" onClick={handleCreateContent} />
                    </RightButtonWrap>
                </FooterButtonWrap>
            </div>
        </ContentsWrap>
    );
};

export default BannerCreate;

export const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

export const HeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

export const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

export const HeaderTitle = styled(MainHeaderTitle)`
    margin: 0;
`;

export const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

export const ContentTitle = styled(TableTitle)`
    margin: 0;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

export const ContentsHeader = styled(ContentTitle)`
    display: flex;
    justify-content: space-between;
`;

export const ContentsMainWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 840px;
    margin: auto;
    padding: 52px 0 80px;
`;

export const InputWrap = styled.div`
    display: flex;
    gap: 12px;
    min-height: 48px;
    width: 100%;
`;

export const LabelWrap = styled.span`
    min-width: 200px;
    padding: 12px 0 12px;
`;

export const LabelInnerWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
export const Label = styled.p`
    color: ${colors.GRAY6};
    ${typo.HEADING_7};
`;

export const InputContentsWrap = styled.div<{ noti?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;

    ${({ noti }) =>
        noti &&
        css`
            flex-direction: column;
            gap: 12px;
        `}
`;

export const FooterButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
`;

export const LeftButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

export const RightButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;
